import { useMemo, useRef, useState } from 'react';
import { Button, Flex, Heading, Link, Text, toaster } from '@optiaxiom/react';
import { IconLink, IconTrashCan, IconXmark } from '@optimizely/axiom-icons';
import { Node } from '@xyflow/react';

import { useWorkflow } from '../../hooks/useWorkflow';
import {
  NodePropertyForm,
  NodePropertyFormRef,
} from '../../nodes/components/forms/NodePropertyForm';
import { NodeIcon } from '../../nodes/components/node-parts/NodeIcon';

import { styles } from './WorkflowRightSidebar.css';

type WorkflowRightSidebarProps = {
  selectedNode: Node | null;
  onClose: () => void;
  onNodeUpdate: (nodeId: string, data: Record<string, unknown>) => void;
  onNodeDelete?: (nodes: Node[]) => void;
  isForced?: boolean;
};

export function WorkflowRightSidebar({
  selectedNode,
  onClose,
  onNodeUpdate,
  onNodeDelete,
  isForced = false,
}: WorkflowRightSidebarProps) {
  const { getNodeConfig } = useWorkflow();
  const formRef = useRef<NodePropertyFormRef>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [operationErrors, setOperationErrors] = useState<Record<string, string>>({});

  const currentNodeAgentId = useMemo(() => {
    if (!selectedNode) return undefined;
    const nodeData = selectedNode.data as Record<string, string>;
    return nodeData?.agent_id || nodeData?.evaluator_agent_id;
  }, [selectedNode]);

  if (!selectedNode) {
    return null;
  }

  const nodeType = getNodeConfig(selectedNode.type || '');
  const requiresAsyncSave = !!nodeType?.onAsyncSave;

  const handleImmediateUpdate = (data: Record<string, unknown>) => {
    onNodeUpdate(selectedNode.id, data);
  };

  const renderNodeForm = () => {
    if (nodeType) {
      return (
        <NodePropertyForm
          ref={formRef}
          nodeId={selectedNode.id}
          nodeType={selectedNode.type || ''}
          initialData={selectedNode.data || {}}
          onParameterChange={!requiresAsyncSave ? handleImmediateUpdate : undefined}
        />
      );
    }
    return (
      <Flex className={styles.noConfigMessage} flexDirection="column">
        <span>No configuration available for this node type.</span>
      </Flex>
    );
  };

  const getNodeTitle = () => {
    if (nodeType) {
      return `${nodeType.description.displayName}`;
    }
    return 'Node Configuration';
  };

  const handleViewAgent = () => {
    if (currentNodeAgentId) {
      const agentUrl = `/agents/specialized/${currentNodeAgentId}`;
      window.open(agentUrl, '_blank');
    }
  };
  const handleSave = async () => {
    if (!formRef.current || isSaving) return;

    setIsSaving(true);
    setOperationErrors({});

    try {
      const { parameters, nodeOperations, nodeTypeDefinition } = formRef.current.getFormData();
      const validationResult = formRef.current.validateAndTransform();

      if (!validationResult.isValid) {
        setIsSaving(false);
        return; // Errors are already set in the form
      }

      // Use React Query mutation for async save if supported by node type
      if (nodeTypeDefinition?.onAsyncSave) {
        try {
          const result = await nodeOperations.saveNodeAsync(parameters);

          // Use the response from async operation for onUpdate if available, otherwise use form data
          const finalData =
            result != null && typeof result === 'object'
              ? { ...(result as Record<string, unknown>) }
              : validationResult.transformed || {};
          onNodeUpdate(selectedNode.id, finalData);
          formRef.current.clearErrors();
          formRef.current.setIsDirty(false);

          // Force refetch to ensure any enableAsyncGet changes are picked up
          nodeOperations.refetch();
          toaster.create('Node configuration saved successfully', { intent: 'success' });
        } catch (error) {
          // If async operation fails, fall back to using current form data
          if (validationResult.transformed) {
            onNodeUpdate(selectedNode.id, validationResult.transformed);
          }
          const errorMessage =
            error instanceof Error ? error.message : 'Failed to save node configuration';
          toaster.create(errorMessage, { intent: 'danger' });
          // Error handled by React Query hook in the form
        }
      } else {
        // For nodes without async save, update immediately as before
        if (validationResult.transformed) {
          onNodeUpdate(selectedNode.id, validationResult.transformed);
          formRef.current.setIsDirty(false);
        }
        toaster.create('Node configuration saved successfully', { intent: 'success' });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save node configuration';
      setOperationErrors({ general: errorMessage });
      toaster.create(errorMessage, { intent: 'danger' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNode || isDeleting) return;

    setIsDeleting(true);
    setOperationErrors({});

    try {
      // Use the enhanced canvas deletion logic
      await onNodeDelete?.([selectedNode]);
      // Clear form errors on successful deletion
      formRef.current?.clearErrors();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete node';
      setOperationErrors({ general: errorMessage });
      toaster.create(errorMessage, { intent: 'danger' });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Flex flexDirection="column" h="full">
      <Flex className={styles.sidebarHeader} flexDirection="row">
        <Flex flexDirection="row" gap="8" alignItems="center">
          <NodeIcon
            Icon={nodeType?.description.iconInfo?.icon}
            color={nodeType?.description.iconInfo?.color}
            nodeType={selectedNode.type || ''}
            variant={selectedNode.type === 'condition' ? 'condition' : undefined}
          />
          <Heading level="4">{getNodeTitle()}</Heading>
          {currentNodeAgentId && (
            <Link onClick={handleViewAgent} className={styles.viewAgentLink}>
              <Flex flexDirection="row" gap="4" alignItems="center">
                <Text fontSize="sm">View Agent</Text>
                <IconLink size="16" />
              </Flex>
            </Link>
          )}
        </Flex>
        <Button
          size="sm"
          appearance="subtle"
          onClick={onClose}
          disabled={isForced && requiresAsyncSave}
        >
          <IconXmark size="16" />
        </Button>
      </Flex>

      <Flex className={styles.sidebarContent} flexDirection="column">
        <div className={styles.formContainer}>{renderNodeForm()}</div>
      </Flex>
      <Flex className={styles.sidebarFooter}>
        <Flex flexDirection="column" gap="2">
          {isForced && requiresAsyncSave && (
            <Text fontSize="sm" color="fg.information">
              Please save this configuration before continuing.
            </Text>
          )}
          <Text fontSize="md" color="fg.error">
            * Required Field
          </Text>
          {Object.keys(operationErrors).length > 0 && (
            <Flex flexDirection="column" gap="2">
              {Object.entries(operationErrors).map(([fieldName, error]) => (
                <Text key={fieldName || error} color="fg.error" fontSize="sm">
                  {error}
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
        <Flex flexDirection="row" gap="8">
          <Button
            appearance="danger-outline"
            size="md"
            onClick={handleDelete}
            loading={isDeleting}
            disabled={requiresAsyncSave && isSaving}
            icon={<IconTrashCan size="16" />}
          />
          {requiresAsyncSave && (
            <Button
              appearance="primary"
              size="md"
              onClick={handleSave}
              loading={isSaving}
              disabled={isDeleting}
            >
              Save
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
